import React from "react"
import styled, { css } from "styled-components"
import { GatsbyImage } from "gatsby-plugin-image"
import { BackgroundImage } from "@abmg-components"

import Licensing from "./licensing"
import Icon from "../global/icon"
import { bankerData } from "@components/global/layout-walled-mb"

const size = {
  tablet: 950,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const HeaderWrapper = styled.div`
  position: relative;
`
const Container = styled.div`
  position: relative;
  margin: 0px auto;
  max-width: 1248px;
  overflow: hidden;
  padding: 0px 48px 32px;
  ${below.mobile`
    padding: 0 24px 16px;
  `}
  h1 {
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 900;
    font-size: 160px;
    line-height: 176px;
    color: white;

    ${below.tablet`
    font-size: 88px;
    line-height: 96px;
  `}
    ${below.mobile`
    font-size: 64px;
    line-height: 64px;
  `}
  &.why-your-name-so-long {
      font-size: 88px;
      line-height: 96px;
      max-width: 756px;
      ${below.tablet`
    font-size: 64px;
    line-height: 72px;
  `}
      ${below.mobile`
    font-size: 48px;
    line-height: 56px;
  `}
    }
  }
  ul {
    padding: 0;
    margin: 0px;
    &.contact-info {
      // margin-top: 16px !important;
      padding: 0 0 0 8px !important;
      border-left: 1px solid #ffffff;
      li {
        font-size: 16px;
        line-height: 20px;
      }
    }

    li {
      color: white;
      font-family: "Playfair Display", serif;
      font-weight: 400;
      font-size: 16px;
      line-height: 20px;
      padding-left: 0;
      &:before {
        display: none;
      }
      &.fullname {
        font-weight: 700 !important;
        font-size: 22px;
        line-height: 28px;
      }
      &.calendly-link {
        display: flex;

        a {
          margin-right: 12px;
        }
        &:hover {
          svg {
            transform: translateX(12px);
          }
        }
        svg {
          height: 20px;
          width: 20px;
          transition: 0.3s ease-in-out;

          .arrow-right-thin {
            stroke: white;
          }
        }
      }
      a {
        color: inherit;
        font-family: inherit;
        :visited {
          color: inherit;
        }
      }
    }
  }
  .team-johnston-contact-info {
    display: flex;
    gap: 96px;
    ${below.mobile`
      flex-direction: column;
      gap: 72px;
    `}
  }
`
const HeadshotContainer = styled.div`
  border-radius: 50%;
  margin-top: 32px;
  overflow: hidden;
  max-height: 312px;
  max-width: 312px;
  border: 16px solid hsla(204, 45%, 98%, 0.35);
  ${below.tablet` 
  max-height: 182px;
    max-width: 182px;
    border: 8px solid hsla(204, 45%, 98%, 0.35);
    `}
  ${below.mobile`
    margin-top: 16px;
    max-height: 128px;
    max-width: 128px;
    border: 4px solid hsla(204, 45%, 98%, .35);
  `}
  img {
    border-radius: 50%;
    background: hsla(204, 45%, 98%, 0.35);
    color: transparent;
  }
  a {
    img {
      color: transparent;
    }
  }
`
const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  z-index: -1;
  width: 100%;
  height: 50%;
  background: linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.6) 0%,
    rgba(135, 135, 135, 0) 100%
  );
`
const DefaultHeadshot = styled.div`
  border-radius: 50%;
  margin-top: 32px;
  overflow: hidden;
  height: 352px;
  width: 352px;
  border: 16px solid hsla(204, 45%, 98%, 0.35);
  display: block;
  background-color: #264765;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 900;
  font-size: 144px;
  line-height: 144px;
  ${below.tablet`
  font-size: 88px;
  line-height: 88px;
  margin-top: 16px;
  max-height: 186px;
  max-width: 186px;
  border: 8px solid hsla(204, 45%, 98%, 0.35);
    `}
  ${below.mobile`
  font-size: 48px;
  line-height: 48px;
  margin-top: 16px;
  max-height: 128px;
  max-width: 128px;
  border: 4px solid hsla(204, 45%, 98%, .35);
  `}
`
const TAODisclosure = styled.div`
  margin-top: 24px;
  color: white;
  font-family: "Playfair Display", serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  ${below.mobile`font-size: 10px; line-height: 16px; margin-top: 16px;`}
`

const AboutHeader = props => {
  const taoDisclosureRequired = () => {
    // If the MB isn't listed in GA use default licensing format
    if (
      !props.bankerData.licenses.includes("GA") &&
      !props.bankerData.licenses.includes("GA TAO")
    ) {
      return false
    }
    // If the MB is only licensed in GA
    else if (props.bankerData.licenses.length < 2) {
      if (props.bankerData.licenses.includes("GA")) {
        return false
      } else {
        return true
      }
    }
    // If the MB is licensed in GA and other states
    else {
      if (props.bankerData.licenses.includes("GA")) {
        return false
      } else {
        return true
      }
    }
  }
  return (
    <>
      <HeaderWrapper>
        <BackgroundImage backgroundImage={props.hero} />
        <Container>
          {props.headshot ? (
            <HeadshotContainer>
              <GatsbyImage
                image={props.headshot}
                // placeholderStyle={{ filter: "blur(8px)" }}
                alt={`Atlantic Bay ${props.bankerData.jobTitle} ${props.bankerData.fullName}, NMLS #${props.bankerData.nmls}`}
              />
            </HeadshotContainer>
          ) : (
            <DefaultHeadshot>{`${props.bankerData.firstName.charAt()}${props.bankerData.lastName.charAt()}`}</DefaultHeadshot>
          )}
          <h1
            className={
              props.bankerData.firstName.length > 12
                ? "why-your-name-so-long"
                : undefined
            }
          >
            {props.bankerData.firstName}
          </h1>
          {props.bankerData.email === "teamjohnston@atlanticbay.com" ? (
            <div className="team-johnston-contact-info">
              <ul>
                <li className="fullname">Kerry Johnston</li>
                <li>Mortgage Banker</li>
                <li>NMLS #1759633</li>
                <li>Licensed in NC & SC</li>
                <ul className="contact-info">
                  <li>
                    <a href={`tel:704-747-2170`}>704-747-2170</a>
                  </li>
                  <li>
                    <a href={`mailto:kerryjohnston@atlanticbay.com`}>
                      kerryjohnston@atlanticbay.com
                    </a>
                  </li>
                  <li className="calendly-link">
                    <a href={props.bankerData.calendly}>
                      {`Schedule a meeting with Kerry`}{" "}
                    </a>
                    <Icon className="arrow" icon="arrow-right-thin" />
                  </li>
                </ul>
              </ul>
              <ul>
                <li className="fullname">Ryan Johnston</li>
                <li>Mortgage Banker</li>
                <li>NMLS #2173563</li>
                <li>Licensed in NC, SC, GA # 2173563</li>
                <ul className="contact-info">
                  <li>
                    <a href={`tel:336-687-5781`}>336-687-5781</a>
                  </li>
                  <li>
                    <a href={`mailto:ryanjohnston@atlanticbay.com`}>
                      ryanjohnston@atlanticbay.com
                    </a>
                  </li>
                  <li className="calendly-link">
                    <a href={props.bankerData.calendly}>
                      {`Schedule a meeting with Ryan`}{" "}
                    </a>
                    <Icon className="arrow" icon="arrow-right-thin" />
                  </li>
                </ul>
              </ul>
            </div>
          ) : (
            <div className="flex flex-col gap-[16px]">
              <ul>
                <li className="fullname">{props.bankerData.fullName}</li>
                <li>{props.bankerData.jobTitle}</li>
                {props.bankerData.slug === "mitchpayne" && (
                  <li>Serving the 30A markets</li>
                )}
                <li>{`NMLS #${props.bankerData.nmls}`}</li>
                <Licensing
                  licenses={props.bankerData.licenses}
                  stateSpecificLicense={props.bankerData.stateSpecificLicense}
                />
              </ul>
              <div className="flex flex-col gap-[16px] min-tablet:items-center min-tablet:flex-row">
                {props.logo && (
                  <div className="max-w-[199px]">
                    {props.logo.images ? (
                      <GatsbyImage
                        image={props.logo}
                        alt={`Atlantic Bay ${props.bankerData.jobTitle} ${props.bankerData.fullName}, NMLS #${props.bankerData.nmls}`}
                      />
                    ) : (
                      <img
                        alt={`Atlantic Bay ${props.bankerData.jobTitle} ${props.bankerData.fullName}, NMLS #${props.bankerData.nmls}`}
                        className="w-full"
                        src={props.logo}
                      />
                    )}
                  </div>
                )}
                <ul className="contact-info">
                  <li>
                    <a href={`tel:${props.bankerData.phone}`}>
                      {props.bankerData.phoneText}
                    </a>
                  </li>
                  <li>
                    <a href={`mailto:${props.bankerData.email}`}>
                      {props.bankerData.email}
                    </a>
                  </li>
                  {props.bankerData.calendly && (
                    <li className="calendly-link">
                      <a href={props.bankerData.calendly}>
                        {`Schedule a meeting with ${props.bankerData.firstName}`}{" "}
                      </a>
                      <Icon className="arrow" icon="arrow-right-thin" />
                    </li>
                  )}
                </ul>
              </div>
              {taoDisclosureRequired() && (
                <TAODisclosure>
                  The individual mentioned is currently unlicensed but under
                  temporary authority with the state of Georgia. This individual
                  has submitted a license application to the Department, and the
                  Department may grant or deny the license application.
                </TAODisclosure>
              )}
            </div>
          )}
        </Container>
        <Overlay />
      </HeaderWrapper>
    </>
  )
}

export default AboutHeader
